import { useState, useMemo, useEffect, type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listMarketplaceListings,
  getMyMarketplaceActivity,
  type MarketplaceListing,
} from "@/lib/game/marketplace.functions";
import { CreateListingDialog } from "@/components/game/CreateListingDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Panel } from "@/components/game/Panel";
import { PortraitFrame } from "@/components/game/PortraitFrame";
import { Pill, RarityTag, TypeBadge } from "@/components/game/Badges";
import {
  FilterPills,
  GoldAmount,
  ItemIcon,
  MicroLabel,
  PageHeader,
  ScreenContainer,
} from "@/components/game/ScreenKit";
import { InfoTip } from "@/components/game/InfoTip";
import { URGENT_MS, creatureTypeOf, formatRemaining, rarityOf } from "@/lib/game/marketplace-view";
import { cn } from "@/lib/utils";
import { Store, Gavel, Hourglass, Loader2, Egg as EggIcon, Search, Tag } from "lucide-react";
import { RouteError, RouteNotFound } from "@/components/game/RouteError";
import { useTutorial } from "@/components/game/TutorialProvider";

export const Route = createFileRoute("/_app/game/marketplace/")({
  component: MarketplacePage,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
});

function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** Das Art-Fenster einer Angebotskarte: Kreatur als Portrait, Ei als Item-Kachel. */
function ListingArt({ l, size }: { l: MarketplaceListing; size: number }) {
  const type = creatureTypeOf(l.snapshot.type, l.snapshot.stats?.type);
  const rarity = rarityOf(l.item_kind, l.snapshot.rarity);
  const img = l.snapshot.image_url ?? null;
  if (l.item_kind !== "monster" && !img) {
    return (
      <ItemIcon
        icon={<EggIcon style={{ width: size * 0.45, height: size * 0.45 }} strokeWidth={2.5} />}
        rarity={rarity ?? "common"}
        size={size}
      />
    );
  }
  return (
    <PortraitFrame
      src={img}
      alt={l.snapshot.name ?? ""}
      size={size}
      ringColor={type ? `var(--type-${type})` : undefined}
    />
  );
}

function ListingCard({ l }: { l: MarketplaceListing }) {
  const { t } = useTranslation("game");
  useNow(1000);

  const isAuction = l.listing_type === "auction";
  const type = creatureTypeOf(l.snapshot.type, l.snapshot.stats?.type);
  const rarity = rarityOf(l.item_kind, l.snapshot.rarity);
  const legendary = rarity === "legendary";

  const hasBid = isAuction && l.current_bid != null;
  const price = isAuction ? (l.current_bid ?? l.starting_price) : l.asking_price;
  // Ohne Gebot steht dort der Startpreis — ihn „Gebot" zu nennen wäre gelogen.
  const priceLabel = isAuction
    ? hasBid
      ? t("marketplace.card.currentBid")
      : t("marketplace.card.startingBid")
    : t("marketplace.card.buyNow");

  const remainingMs = l.ends_at ? new Date(l.ends_at).getTime() - Date.now() : null;
  const urgent = remainingMs != null && remainingMs > 0 && remainingMs < URGENT_MS;

  return (
    <Link
      to="/game/marketplace/$listingId"
      params={{ listingId: l.id }}
      className="group block text-foreground no-underline hover:text-foreground"
    >
      <Panel
        tone={legendary ? "boss" : "default"}
        holo={legendary}
        grid
        className="h-full transition-transform group-hover:-translate-y-0.5"
        contentClassName="flex h-full flex-col gap-2.5 p-3"
      >
        <div className="flex gap-2.5">
          <ListingArt l={l} size={64} />
          <div className="min-w-0 flex-1">
            <div className="line-clamp-2 text-sm font-extrabold leading-tight [overflow-wrap:anywhere]">
              {l.snapshot.name ?? "—"}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {type ? <TypeBadge type={type} /> : null}
              {rarity ? <RarityTag rarity={rarity} /> : null}
              {l.item_kind === "monster" && l.snapshot.level != null ? (
                <span className="text-[11px] font-extrabold tabular-nums text-muted-foreground">
                  {t("marketplace.card.level")} {l.snapshot.level}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-auto flex items-end justify-between gap-2 pt-1">
          <div className="min-w-0">
            <MicroLabel className="flex items-center gap-1">
              {isAuction ? (
                <Gavel className="h-3 w-3 shrink-0" strokeWidth={2.5} />
              ) : (
                <Store className="h-3 w-3 shrink-0" strokeWidth={2.5} />
              )}
              <span className="min-w-0 truncate">
                {t(`marketplace.listingType.${l.listing_type}`)}
                {isAuction ? ` · ${priceLabel}` : ""}
              </span>
            </MicroLabel>
            {price != null ? (
              <GoldAmount value={price} size="lg" className="mt-0.5" />
            ) : (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </div>
          <div className="min-w-0 shrink text-right text-[11px] text-muted-foreground">
            <div className="truncate">
              {t("marketplace.card.by")} {l.seller_name ?? "?"}
            </div>
            {isAuction ? (
              <div
                className={cn(
                  "mt-0.5 inline-flex items-center justify-end gap-1 font-extrabold tabular-nums",
                  urgent && "text-primary",
                )}
              >
                <Hourglass className="h-3 w-3 shrink-0" strokeWidth={2.5} />
                {formatRemaining(l.ends_at, t)}
              </div>
            ) : null}
          </div>
        </div>
      </Panel>
    </Link>
  );
}

/** Ein Raster aus Angebotskarten — im Markt, in „Aktive Angebote" und im Verlauf gleich. */
function ListingGrid({ listings }: { listings: MarketplaceListing[] }) {
  return (
    <div
      className="grid gap-3.5"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}
    >
      {listings.map((l) => (
        <ListingCard key={l.id} l={l} />
      ))}
    </div>
  );
}

/** Ruhige Fläche für „lädt", „leer" und „Fehler" — nie stillschweigend leer wirken. */
function StateNote({ children }: { children: ReactNode }) {
  return (
    <Panel contentClassName="flex items-center justify-center gap-2 px-3 py-10 text-sm text-muted-foreground">
      {children}
    </Panel>
  );
}

function BrowseTab() {
  const { t } = useTranslation("game");
  const { t: tc } = useTranslation("common");
  const [itemKind, setItemKind] = useState<"all" | "monster" | "egg">("all");
  const [listingType, setListingType] = useState<"all" | "fixed" | "auction">("all");
  const [sort, setSort] = useState<"new" | "price_asc" | "price_desc" | "ends_soon">("new");
  const [search, setSearch] = useState("");

  const fetcher = useServerFn(listMarketplaceListings);
  const q = useQuery({
    queryKey: ["marketplace", "list", itemKind, listingType, sort, search],
    queryFn: () => fetcher({ data: { itemKind, listingType, sort, search } }),
    refetchInterval: 30_000,
  });

  const listings = q.data?.listings ?? [];

  return (
    <div className="space-y-3.5">
      <div className="flex flex-wrap items-end gap-2.5">
        <div className="relative min-w-[190px] flex-1 sm:max-w-xs">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            strokeWidth={2.5}
          />
          <Input
            placeholder={t("marketplace.filters.search")}
            aria-label={t("marketplace.filters.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="min-w-[150px] flex-1 sm:flex-none">
          <MicroLabel className="mb-1">{t("marketplace.create.listingType")}</MicroLabel>
          <Select
            value={listingType}
            onValueChange={(v) => setListingType(v as typeof listingType)}
          >
            <SelectTrigger
              className="sm:w-[150px]"
              aria-label={t("marketplace.create.listingType")}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("marketplace.filters.all")}</SelectItem>
              <SelectItem value="fixed">{t("marketplace.filters.fixed")}</SelectItem>
              <SelectItem value="auction">{t("marketplace.filters.auction")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[170px] flex-1 sm:flex-none">
          <MicroLabel className="mb-1">{t("marketplace.filters.sort")}</MicroLabel>
          <Select value={sort} onValueChange={(v) => setSort(v as typeof sort)}>
            <SelectTrigger className="sm:w-[190px]" aria-label={t("marketplace.filters.sort")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">{t("marketplace.filters.sortNew")}</SelectItem>
              <SelectItem value="price_asc">{t("marketplace.filters.sortPriceAsc")}</SelectItem>
              <SelectItem value="price_desc">{t("marketplace.filters.sortPriceDesc")}</SelectItem>
              <SelectItem value="ends_soon">{t("marketplace.filters.sortEndsSoon")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <FilterPills
        value={itemKind}
        onChange={setItemKind}
        items={[
          ["all", t("marketplace.filters.all")],
          ["monster", t("marketplace.filters.monsters")],
          ["egg", t("marketplace.filters.eggs")],
        ]}
      />

      {q.isLoading ? (
        <StateNote>
          <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2.5} />
        </StateNote>
      ) : q.isError ? (
        // Ein Netz-/Serverfehler ist KEIN leerer Markt — sonst behauptet die
        // Seite, es gäbe nichts zu kaufen, obwohl sie nur nichts laden konnte.
        <StateNote>{tc("errors.generic")}</StateNote>
      ) : listings.length === 0 ? (
        <StateNote>{t("marketplace.empty")}</StateNote>
      ) : (
        <ListingGrid listings={listings} />
      )}
    </div>
  );
}

/** Eine Zeile in „Meine Gebote" / „Transaktionen". */
function ActivityRow({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-center justify-between gap-3 border-t-2 border-ink px-3 py-2.5 first:border-t-0">
      {children}
    </li>
  );
}

function MineTab() {
  const { t } = useTranslation("game");
  const { t: tc } = useTranslation("common");
  const fetcher = useServerFn(getMyMarketplaceActivity);
  const q = useQuery({ queryKey: ["marketplace", "mine"], queryFn: () => fetcher() });

  const own = useMemo(() => q.data?.ownListings ?? [], [q.data]);
  const active = own.filter((l) => l.status === "active");
  const history = own.filter((l) => l.status !== "active");
  const tx = q.data?.transactions ?? [];
  const myBids = q.data?.myBids ?? [];
  const feesTotal = own.reduce((s, l) => s + (l.listing_fee_paid ?? 0), 0);
  const feesActive = active.reduce((s, l) => s + (l.listing_fee_paid ?? 0), 0);

  // Richtung einer Transaktion: Gehört das Listing mir, habe ich verkauft.
  // `seller_id` ist auf JEDER Zeile gesetzt (beide Seiten stehen darin) und
  // taugt deshalb nicht zur Unterscheidung.
  const ownListingIds = useMemo(() => new Set(own.map((l) => l.id)), [own]);

  if (q.isLoading) {
    return (
      <StateNote>
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2.5} />
      </StateNote>
    );
  }
  if (q.isError) {
    return <StateNote>{tc("errors.generic")}</StateNote>;
  }

  return (
    <div className="space-y-6">
      <Panel tone="secondary" contentClassName="flex flex-wrap items-center gap-x-6 gap-y-2 p-3">
        <div>
          <MicroLabel>
            <InfoTip text={t("marketplace.create.feeHint")}>
              <span>{t("marketplace.mine.feesTotal")} ⓘ</span>
            </InfoTip>
          </MicroLabel>
          <GoldAmount value={feesTotal} size="md" className="mt-0.5" />
        </div>
        <div>
          <MicroLabel>{t("marketplace.mine.feesActive")}</MicroLabel>
          <GoldAmount value={feesActive} size="md" className="mt-0.5" />
        </div>
      </Panel>

      <section>
        <h3 className="mb-2.5 text-[17px]">
          {t("marketplace.mine.active")} · {active.length}
        </h3>
        {active.length === 0 ? (
          <StateNote>{t("marketplace.mine.empty")}</StateNote>
        ) : (
          <ListingGrid listings={active} />
        )}
      </section>

      <section>
        <h3 className="mb-2.5 text-[17px]">
          {t("marketplace.mine.myBids")} · {myBids.filter((b) => !b.refunded_at).length}
        </h3>
        {myBids.length === 0 ? (
          <StateNote>{t("marketplace.mine.empty")}</StateNote>
        ) : (
          <Panel contentClassName="p-0">
            <ul>
              {myBids.map((b) => {
                const l = (b as { marketplace_listings?: MarketplaceListing }).marketplace_listings;
                return (
                  <ActivityRow key={b.id}>
                    <div className="min-w-0">
                      {l ? (
                        <Link
                          to="/game/marketplace/$listingId"
                          params={{ listingId: l.id }}
                          className="line-clamp-2 text-sm font-extrabold [overflow-wrap:anywhere] hover:underline"
                        >
                          {l.snapshot.name ?? "—"}
                        </Link>
                      ) : (
                        <span className="text-sm font-extrabold">—</span>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                        <span className="tabular-nums">
                          {new Date(b.created_at).toLocaleString()}
                        </span>
                        {b.refunded_at ? (
                          <Pill
                            style={{ background: "var(--secondary)", color: "var(--foreground)" }}
                          >
                            {t("marketplace.mine.refunded")}
                          </Pill>
                        ) : null}
                      </div>
                    </div>
                    <GoldAmount value={b.amount} size="md" className="shrink-0" />
                  </ActivityRow>
                );
              })}
            </ul>
          </Panel>
        )}
      </section>

      <section>
        <h3 className="mb-2.5 text-[17px]">
          {t("marketplace.mine.transactions")} · {tx.length}
        </h3>
        {tx.length === 0 ? (
          <StateNote>{t("marketplace.mine.empty")}</StateNote>
        ) : (
          <Panel contentClassName="p-0">
            <ul>
              {tx.map((x) => {
                // Ohne Listing-Bezug lässt sich die Richtung nicht belegen —
                // dann wird sie auch nicht behauptet.
                const listingId = x.listing_id;
                const known = listingId != null;
                const sold = listingId != null && ownListingIds.has(listingId);
                const kindLabel =
                  x.item_kind === "egg"
                    ? t("marketplace.filters.eggs")
                    : x.item_kind === "monster"
                      ? t("marketplace.filters.monsters")
                      : x.item_kind;
                return (
                  <ActivityRow key={x.id}>
                    <div className="min-w-0">
                      <div className="text-sm font-extrabold">{kindLabel}</div>
                      <div className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
                        {new Date(x.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      {/* Verkäufer sehen ihren Netto-Erlös, Käufer den bezahlten
                          Preis — amount_net ist immer der Betrag des Verkäufers. */}
                      <GoldAmount value={sold ? x.amount_net : x.amount_gross} size="md" />
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        {known ? (
                          <span className="font-extrabold">
                            {sold ? t("marketplace.mine.sold") : t("marketplace.mine.bought")}
                          </span>
                        ) : null}
                        {sold ? (
                          <InfoTip text={t("marketplace.create.saleFeeHint")}>
                            <span className="ml-1.5 tabular-nums">−{x.fee} ⓘ</span>
                          </InfoTip>
                        ) : null}
                      </div>
                    </div>
                  </ActivityRow>
                );
              })}
            </ul>
          </Panel>
        )}
      </section>

      {history.length > 0 && (
        <section>
          <h3 className="mb-2.5 text-[17px]">
            {t("marketplace.mine.history")} · {history.length}
          </h3>
          <ListingGrid listings={history} />
        </section>
      )}
    </div>
  );
}

function MarketplacePage() {
  useTutorial("marketplace_intro");
  const { t } = useTranslation("game");
  return (
    <ScreenContainer>
      <PageHeader
        title={t("marketplace.title")}
        icon={<Store className="h-[22px] w-[22px] text-primary" strokeWidth={2.5} />}
        sub={t("marketplace.subtitle")}
        actions={
          <CreateListingDialog
            trigger={
              <Button variant="outline" size="sm">
                <Tag strokeWidth={2.5} />
                {t("marketplace.create.trigger")}
              </Button>
            }
          />
        }
      />
      <Tabs defaultValue="browse">
        <TabsList>
          <TabsTrigger value="browse">{t("marketplace.tabs.browse")}</TabsTrigger>
          <TabsTrigger value="mine">{t("marketplace.tabs.mine")}</TabsTrigger>
        </TabsList>
        <TabsContent value="browse" className="mt-3.5">
          <BrowseTab />
        </TabsContent>
        <TabsContent value="mine" className="mt-3.5">
          <MineTab />
        </TabsContent>
      </Tabs>
    </ScreenContainer>
  );
}
